/**
 * Language Context Provider
 * Manages language state and persistence
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);

  if (context === null) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }

  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('ru'); // Default to Russian as in ExerciseDetail
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // API base URL for consistent API calls
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // Get auth token from localStorage
  const getAuthToken = () => {
    return localStorage.getItem('access_token') || localStorage.getItem('token');
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!getAuthToken();
  };

  // Load user's language preference from server
  const loadUserLanguagePreference = useCallback(async () => {
    if (!isAuthenticated()) {
      // For unauthenticated users, check localStorage or default to Russian
      const localLanguage = localStorage.getItem('language') || 'ru';
      setLanguage(localLanguage);
      setIsLoading(false);
      return;
    }

    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/ui-preferences`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const savedLanguage = data.language || 'ru';
        setLanguage(savedLanguage);
        setError(null);
      } else if (response.status === 401) {
        // Token expired or invalid, fall back to local storage
        const localLanguage = localStorage.getItem('language') || 'ru';
        setLanguage(localLanguage);
      } else {
        throw new Error(`Failed to load language preference: ${response.status}`);
      }
    } catch (err) {
      console.warn('Failed to load user language preference:', err);
      // Fall back to localStorage or Russian
      const fallbackLanguage = localStorage.getItem('language') || 'ru';
      setLanguage(fallbackLanguage);
      // Don't show error for missing API endpoints, only for actual network issues
      if (err.message.includes('Network') || err.message.includes('fetch')) {
        setError('Using saved language - will sync when connection is restored');
      } else {
        setError(null); // Don't show errors for missing API endpoints
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save language preference to server
  const saveLanguagePreference = useCallback(async (newLanguage) => {
    // Always save locally first
    localStorage.setItem('language', newLanguage);

    if (!isAuthenticated()) {
      // For unauthenticated users, only save locally
      return;
    }

    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/ui-preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ language: newLanguage }),
      });

      if (response.ok) {
        setError(null); // Clear any previous errors
      } else if (response.status === 401) {
        // Token expired, local storage already updated
        console.warn('Token expired, language saved locally');
        setError(null); // Don't show error for expired tokens
      } else if (response.status === 404) {
        // API endpoint doesn't exist yet, language saved locally
        console.warn('Language API not implemented yet, saved locally');
        setError(null); // Don't show error for missing API
      } else {
        throw new Error(`Server error: ${response.status}`);
      }
    } catch (err) {
      // Network or other errors - local storage already updated
      console.warn('Failed to sync language preference to server:', err.message);
      // Only show error for actual network problems, not missing API
      if (err.message.includes('Network') || err.message.includes('fetch')) {
        setError('Language saved locally - will sync when connection is restored');
      } else {
        setError(null); // Don't show errors for missing API endpoints
      }
    }
  }, []);

  // Toggle between Russian and English
  const toggleLanguage = useCallback(() => {
    const newLanguage = language === 'ru' ? 'en' : 'ru';
    setLanguage(newLanguage);

    // Save preference (with debouncing to avoid rapid API calls)
    const timeoutId = setTimeout(() => {
      saveLanguagePreference(newLanguage);
    }, 300);

    // Clean up previous timeout
    return () => clearTimeout(timeoutId);
  }, [language, saveLanguagePreference]);

  // Set specific language
  const setLanguageValue = useCallback((newLanguage) => {
    if (newLanguage !== 'ru' && newLanguage !== 'en') {
      console.warn(`Invalid language: ${newLanguage}. Must be 'ru' or 'en'`);
      return;
    }

    setLanguage(newLanguage);
    saveLanguagePreference(newLanguage);
  }, [saveLanguagePreference]);

  // Load language on mount and when authentication state changes
  useEffect(() => {
    loadUserLanguagePreference();
  }, [loadUserLanguagePreference]);

  // Listen for authentication changes (user login/logout)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'access_token' || e.key === 'token') {
        // Auth state changed, reload language preference
        loadUserLanguagePreference();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [loadUserLanguagePreference]);

  // Listen for focus events to sync language across tabs
  useEffect(() => {
    const handleFocus = () => {
      if (isAuthenticated()) {
        loadUserLanguagePreference();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [loadUserLanguagePreference]);

  // Initialize language on first load
  useEffect(() => {
    // Apply the initial language immediately
    const initialLanguage = localStorage.getItem('language') || 'ru';
    setLanguage(initialLanguage);
  }, []);

  const value = {
    language,
    isLoading,
    error,
    toggleLanguage,
    setLanguage: setLanguageValue,
    isAuthenticated: isAuthenticated(),
    refreshLanguage: loadUserLanguagePreference,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Export LanguageContext for direct access if needed
export { LanguageContext };